import { useEffect, useRef, useState } from 'react';
import AddressBlock from './AddressBlock';
import FavoriteProductCard from './FavoriteProductCard';
import favoritesBlackIcon from '../assets/избранное-чёрное.png';
import { apiClient } from '../api/client';
import { getProductImageUrl } from '../utils/imageUrl';
import type { Product } from '../api/types';

interface FavoritesProps {
  userId: string | null;
  onLogoClick?: () => void;
  onCatalogToggle?: () => void;
  isCatalogOpen?: boolean;
  onFavoritesClick?: () => void;
  onCartClick?: () => void;
  onProductClick?: (productId: string) => void;
  onFavoriteRemoved?: (productId: string) => void;
}

export default function Favorites({
  userId,





  onProductClick,
  onFavoriteRemoved,
}: FavoritesProps) {
  const addressRef = useRef<HTMLDivElement>(null);
  const [favoriteProducts, setFavoriteProducts] = useState<Product[]>([]);
  const [cartQuantities, setCartQuantities] = useState<Record<string, number>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fallbackProductImage = 'https://www.figma.com/api/mcp/asset/832444b1-14a6-4da8-8aed-afa1b4f5718c';

  useEffect(() => {
    const loadFavorites = async () => {
      if (!userId) {
        setIsLoading(false);
        setError(null);
        setFavoriteProducts([]);
        setCartQuantities({});
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const favoritesResponse = await apiClient.getUserFavorites(userId);
        if (!favoritesResponse.success || !favoritesResponse.data) {
          throw new Error(favoritesResponse.error || 'Failed to load favorites');
        }

        const productIds = favoritesResponse.data;
        if (productIds.length === 0) {
          setFavoriteProducts([]);
          setCartQuantities({});
          setIsLoading(false);
          return;
        }

        const productsResponse = await apiClient.getProductsBatch(productIds);
        if (!productsResponse.success || !productsResponse.data) {
          throw new Error(productsResponse.error || 'Failed to load products');
        }

        setFavoriteProducts(productsResponse.data);

        const cartResponse = await apiClient.getUserCart(userId);
        if (cartResponse.success && cartResponse.data) {
          const quantityMap: Record<string, number> = {};
          for (const item of cartResponse.data.items || []) {
            quantityMap[item.product_id] = item.quantity;
          }
          setCartQuantities(quantityMap);
        } else {
          setCartQuantities({});
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
        console.error('Error loading favorites:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadFavorites();
  }, [userId]);

  const handleRemoveFromFavorites = async (productId: string) => {
    if (!userId) {
      alert('Необходимо войти в систему');
      return;
    }

    try {
      const response = await apiClient.removeFromFavorites(userId, productId);
      if (!response.success) {
        throw new Error(response.error || 'Failed to remove from favorites');
      }

      setFavoriteProducts((prev) => prev.filter((p) => p.id !== productId));
      onFavoriteRemoved?.(productId);
    } catch (err) {
      console.error('Error removing from favorites:', err);
      alert(err instanceof Error ? err.message : 'Не удалось удалить из избранного');
    }
  };

  const handleAddToCart = async (productId: string) => {
    if (!userId) {
      alert('Необходимо войти в систему');
      return;
    }

    try {
      const response = await apiClient.addToCart(userId, productId, 1);
      if (!response.success) {
        throw new Error(response.error || 'Failed to add to cart');
      }

      setCartQuantities((prev) => ({ ...prev, [productId]: 1 }));
    } catch (err) {
      console.error('Error adding to cart:', err);
      alert(err instanceof Error ? err.message : 'Не удалось добавить в корзину');
    }
  };

  const handleIncreaseCart = async (productId: string) => {
    if (!userId) return;

    const currentQty = cartQuantities[productId] || 0;
    const nextQty = currentQty + 1;

    try {
      const response = await apiClient.updateCartItem(userId, productId, nextQty);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update quantity');
      }
      setCartQuantities((prev) => ({ ...prev, [productId]: nextQty }));
    } catch (err) {
      console.error('Error increasing quantity:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить количество');
    }
  };

  const handleDecreaseCart = async (productId: string) => {
    if (!userId) return;

    const currentQty = cartQuantities[productId] || 0;
    if (currentQty <= 0) return;

    try {
      if (currentQty === 1) {
        const response = await apiClient.removeFromCart(userId, productId);
        if (!response.success) {
          throw new Error(response.error || 'Failed to remove from cart');
        }

        setCartQuantities((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
        return;
      }

      const nextQty = currentQty - 1;
      const response = await apiClient.updateCartItem(userId, productId, nextQty);
      if (!response.success) {
        throw new Error(response.error || 'Failed to update quantity');
      }

      setCartQuantities((prev) => ({ ...prev, [productId]: nextQty }));
    } catch (err) {
      console.error('Error decreasing quantity:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить количество');
    }
  };

  return (
    <div className="relative flex-1 bg-[#f3f3f3]">
      <main className="relative mx-auto w-full max-w-[1520px] px-4 pt-[20px] pb-[32px] sm:px-6 lg:px-8">
        <div className="mb-[20px]">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex items-center gap-4 pl-1 sm:pl-3 lg:pl-5">
              <img src={favoritesBlackIcon} alt="Избранное" className="h-[38px] w-[38px] flex-shrink-0 sm:h-[46px] sm:w-[46px] lg:h-[50px] lg:w-[50px]" />
              <h1 className="text-[30px] font-extrabold leading-none tracking-[1px] text-black sm:text-[36px] lg:text-[40px] lg:tracking-[2px]">
                Избранное
              </h1>
            </div>

            {userId && (
              <div ref={addressRef} className="w-full max-w-[360px]" style={{ height: '80px' }}>
                <AddressBlock userId={userId} className="h-full" />
              </div>
            )}
          </div>
        </div>

        {!userId ? (
          <div className="py-20 text-center">
            <p className="text-[20px] text-gray-600">Избранное пусто</p>
          </div>
        ) : isLoading ? (
          <div className="py-20 text-center">
            <p className="text-[20px] text-gray-600">Загрузка избранного...</p>
          </div>
        ) : error ? (
          <div className="py-20 text-center">
            <p className="text-[20px] text-red-600">Ошибка: {error}</p>
          </div>
        ) : favoriteProducts.length === 0 ? (
            <div className="py-20 text-center">
            <p className="text-[20px] text-gray-600">Избранное пусто</p>
            </div>
          ) : (
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fit,minmax(220px,1fr))] sm:gap-5 xl:grid-cols-[repeat(auto-fit,minmax(260px,1fr))] xl:gap-6">
            {favoriteProducts.map((product, index) => (
              <div key={product.id} style={{ animationDelay: `${index * 0.1}s` }} className="animate-slide-up-fade">
                <FavoriteProductCard
                  image={getProductImageUrl(product.primary_image || product.primary_thumbnail, fallbackProductImage)}
                  title={product.name}
                  price={product.price}
                  cartQuantity={cartQuantities[product.id] || 0}
                  onRemove={() => handleRemoveFromFavorites(product.id)}
                  onAddToCart={() => handleAddToCart(product.id)}
                  onIncreaseCart={() => handleIncreaseCart(product.id)}
                  onDecreaseCart={() => handleDecreaseCart(product.id)}
                  onProductClick={() => onProductClick?.(product.id)}
                />
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

